import "./index.css";

import { useQuery } from "@apollo/client";
import { Italiana } from "next/font/google";
import { useRouter } from "next/router";
import React from "react";
import Modal, { ModalProps } from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";

import INSPIRE from "@/graphql/queries/inspire";
import useTracker from "@/hooks/useTracker";
import { searchResultRoute } from "@/utils/search";

import Question from "./question";

const italiana = Italiana({
  subsets: ["latin"],
  weight: "400",
});

interface SearchModalProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ModalProps {
  initSearch: string;
  onNewSearch?: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({
  show = true,
  onHide,
  initSearch,
  onNewSearch,
}) => {
  const [localInitSearch] = React.useState(initSearch);
  const [searchValue, setSearchValue] = React.useState(initSearch);
  const [selectedFilters, setSelectedFilters] = React.useState<{
    [key in string]: string[];
  }>({});
  const [isChanged, setIsChanged] = React.useState(false);
  const router = useRouter();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [isTextareaFocused, setIsTextareFocused] = React.useState(false);
  const [isSearchLoading, setIsSearchLoading] = React.useState(false);
  const { searchEvent, smartFilterClickEvent } = useTracker();

  React.useEffect(() => {
    setSearchValue(initSearch);
  }, [initSearch, show]);

  const { data, loading, error } = useQuery(INSPIRE, {
    variables: { query: initSearch || "" },
    skip: localInitSearch !== initSearch && !show,
  });

  const questions: { question: string; filters: string[] }[] =
    React.useMemo(() => {
      return data?.inspire || [];
    }, [data]);

  React.useEffect(() => {
    if (show) {
      smartFilterClickEvent({ query: initSearch, preload: !!data });
    }
  }, [show]);

  const parseFiltersToSearch = () => {
    let filters = Object.keys(selectedFilters).reduce(
      (acc, cur, index, arr) => {
        const str = selectedFilters[cur];
        if (!str || str.length === 0) {
          return acc;
        } else {
          return acc + str.join(", ") + (index !== arr.length - 1 ? ", " : "");
        }
      },
      ""
    );
    if (filters[filters.length - 1] === " ") {
      filters = filters.substring(0, filters.length - 2);
    }
    return filters;
  };

  const finalSearch = () => {
    const filters = parseFiltersToSearch();
    if (!searchValue) {
      return filters;
    } else if (!filters) {
      return searchValue;
    } else {
      return searchValue + ", " + filters;
    }
  };

  const handleSearch = async () => {
    if (!isChanged) return;
    if (router) {
      searchEvent({
        query: finalSearch(),
        smart_filter_content: parseFiltersToSearch(),
        original_query: searchValue,
      });
      setIsSearchLoading(true);
      await router.push(searchResultRoute(finalSearch()));
      if (onHide) onHide();
      setIsSearchLoading(false);
      setSelectedFilters({});
    }
  };

  const handleNewSearch = () => {
    if (onHide) onHide();
    onNewSearch && onNewSearch();
  };

  const handleTextareaChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value: newValue } = event.target;
    setSearchValue(newValue);

    event.target.style.height = "24px"; // Reset the height
    if (newValue) {
      event.target.style.height = event.target.scrollHeight + "px";
    }
  };

  React.useEffect(() => {
    if (!isChanged && searchValue !== initSearch) {
      setIsChanged(true);
    }
  }, [searchValue, isChanged, initSearch]);

  React.useEffect(() => {
    if (!isChanged && Object.keys(selectedFilters).length !== 0) {
      setIsChanged(true);
    }
  }, [selectedFilters, isChanged]);

  React.useEffect(() => {
    if (!textareaRef || !textareaRef.current) return;
    const searchTextareaRef = textareaRef.current;
    if (searchTextareaRef) {
      if (!isTextareaFocused) {
        searchTextareaRef.style.height = "24px";
        searchTextareaRef.scrollTop = 0;
      } else {
        searchTextareaRef.style.height = searchTextareaRef.scrollHeight + "px";
        searchTextareaRef.focus();
        searchTextareaRef.scrollTop = searchTextareaRef.scrollHeight;
        const len = searchTextareaRef.value.length;
        searchTextareaRef.setSelectionRange(len, len);
      }
    }
  }, [isTextareaFocused]);

  return (
    <>
      <Modal
        show={show}
        style={{ zIndex: 999999 }}
        dialogClassName="search-modal-dialog"
        contentClassName="search-modal-content"
        onHide={onHide}
      >
        <div className="search-modal-inner">
          <div className="search-section">
            <img
              src="/search-icon.svg"
              alt="Search Icon"
              className="search-icon"
            />
            <img
              src="/back.svg"
              alt="Back"
              className="back-icon"
              onClick={onHide}
            />
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="search-input"
              placeholder='Search things like "dressy all white dress, not too formal"'
            ></input>
            <textarea
              value={searchValue}
              ref={textareaRef}
              onChange={handleTextareaChange}
              className="search-textarea"
              placeholder='Search things like "dressy all white dress, not too formal"'
              onFocus={() => setIsTextareFocused(true)}
              onBlur={() => setIsTextareFocused(false)}
            />
            <img
              src="/icon-x.svg"
              alt="Close Icon"
              className={`close-icon close-icon-web`}
              onClick={onHide}
            />
            <img
              src="/icon-x.svg"
              alt="Close Icon"
              className={`close-icon close-icon-mobile ${
                searchValue ? "visiable" : ""
              }`}
              onClick={() => {
                setSearchValue("");
              }}
            />
          </div>
          {loading ? (
            <div className="loading-section">
              <Spinner
                animation="border"
                role="status"
                className="loading-spinner"
              ></Spinner>
              <div className={`loading-text ${italiana.className}`}>
                Loading smart filters...
              </div>
            </div>
          ) : error ? (
            <div className="error-section">
              <div className={`section-title ${italiana.className}`}>
                Oops! Something has gone wrong.
              </div>
              <div className="section-content">
                The feature is currently in Beta, so errors might happens.
                Please try again letter.
              </div>
            </div>
          ) : (
            <>
              <div className="questions-section">
                <div className={`section-title `}>
                  Refine your search with any of these optional questions. Don‘t
                  see what you need? Just type your own words into the search
                  box!
                </div>
                {questions.map((item) => {
                  return (
                    <Question
                      key={item.question}
                      {...item}
                      selectedFilters={selectedFilters[item.question]}
                      setSelectedFilters={setSelectedFilters}
                    />
                  );
                })}
              </div>
              <div className="bottom-section">
                <div className="flex flex-auto w-100">
                  <div className="new-search" onClick={handleNewSearch}>
                    <img className="new-search-icon" src="/plus.svg" />
                    <div className="new-search-text">New Search</div>
                  </div>
                </div>
                <div className="search-buttons">
                  <div
                    className={`search ${!isChanged ? "disabled" : ""}`}
                    onClick={handleSearch}
                  >
                    {!isSearchLoading ? (
                      "Search"
                    ) : (
                      <Spinner animation="border" role="status" size="sm">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
      <style jsx>{`
        textarea:placeholder-shown {
          text-overflow: ellipsis;
        }

        textarea::placeholder {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
        .search-modal-inner {
          height: 100%;
          max-height: calc(100vh - 240px);
          display: flex;
          flex-direction: column;
        }
        .loading-section {
          width: 100%;
          text-align: center;
          padding: 64px 32px;

          .loading-text {
            color: #191919;
            font-size: 36px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;

            margin-top: 16px;
          }
        }
        .error-section {
          width: 100%;
          text-align: center;
          padding: 51px 24px;

          .section-title {
            color: #191919;
            text-align: center;
            font-size: 32px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
            margin-bottom: 16px;
          }

          .section-content {
            color: #666;
            text-align: center;
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            line-height: 130%; /* 18.2px */
          }
        }
        .search-section {
          display: flex;
          align-items: center;
          padding: 24px 32px;
          border-bottom: 1px solid #e7e7e7;

          .search-icon {
            width: 24px;
            height: 24px;
          }

          .back-icon {
            display: none;
          }

          .search-input {
            width: 100%;
            margin: 0 16px;
            padding-left: 16px;
            border-left: 1px solid #e5e5e5;

            color: #191919;
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: 24px; /* 150% */

            &:focus {
              outline: none;
            }
          }

          .search-textarea {
            display: none;
          }

          .close-icon {
            width: 24px;
            height: 24px;
            cursor: pointer;
          }

          .close-icon-mobile {
            display: none;
          }
        }

        .questions-section {
          padding: 24px 32px 0;
          overflow: auto;
          height: 100%;
          .section-title {
            color: #666;
            font-size: 15px;
            font-style: normal;
            font-weight: 500;
            line-height: 100%; /* 15px */

            margin-bottom: 32px;
          }
        }

        .bottom-section {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 32px;
          border-top: 1px solid #e7e7e7;

          .exit {
            display: flex;
            align-items: center;
            border: 1px solid #e7e7e7;
            padding: 12px 16px 12px 12px;
            cursor: pointer;
            margin-right: 16px;

            .exit-icon {
              width: 16px;
              height: 16px;
              margin-right: 8px;
            }
            .exit-text {
              color: #191919;
              font-size: 14px;
              font-style: normal;
              font-weight: 700;
              line-height: normal;
              letter-spacing: -0.28px;
            }
          }

          .new-search {
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #e7e7e7;
            padding: 12px 16px 12px 12px;
            cursor: pointer;
            margin-right: 16px;
            .new-search-icon {
              width: 16px;
              height: 16px;
              margin-right: 8px;
            }
            .new-search-text {
              color: #191919;
              font-size: 14px;
              font-style: normal;
              font-weight: 700;
              line-height: normal;
              letter-spacing: -0.28px;
            }
          }

          .search-buttons {
            display: flex;
            align-items: center;
            .search {
              color: #fff;
              font-size: 14px;
              font-style: normal;
              font-weight: 700;
              line-height: normal;
              letter-spacing: -0.28px;
              padding: 12px 16px;
              background: #191919;

              text-align: center;
              min-width: 79px;
              margin-right: 16px;
              cursor: pointer;

              &.disabled {
                color: #999;
                cursor: default;
                background: #f7f7f7;
              }
            }
          }
        }
        @media (max-width: 768px) {
          .search-modal-inner {
            height: 100%;
            max-height: 100%;
          }

          .search-section {
            padding: 16px;
            .search-icon {
              display: none;
            }

            .back-icon {
              width: 16px;
              height: 16px;
              display: block;
            }

            .search-input {
              display: none;
            }

            .search-textarea {
              display: block;
              width: 100%;
              margin: 0 16px;
              padding-left: 16px;
              border-left: 1px solid #e5e5e5;
              height: 24px;
              max-height: 80px;
              min-height: 24px;
              border-radius: 0;

              color: #191919;
              font-size: 14px;
              font-style: normal;
              font-weight: 500;
              line-height: 20px; /* 142.857% */

              &:focus {
                outline: none;
              }
            }

            .close-icon-web {
              display: none;
            }
            .close-icon-mobile {
              display: block;
              visibility: hidden;
              &.visiable {
                visibility: visible;
              }
            }
          }

          .questions-section {
            padding: 24px;

            .section-title {
              line-height: 130%; /* 19.5px */
            }
          }

          .bottom-section {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .exit {
              display: none;
            }

            .new-search {
              flex: auto;
              width: 50%;
            }

            .search-buttons {
              width: 100%;
              flex-direction: row-reverse;
              justify-content: space-between;
              .search {
                flex: auto;
                width: 50%;
                margin-right: 0;
                margin-left: 8px;
                text-align: center;
              }
            }
          }
        }
      `}</style>
    </>
  );
};

export default SearchModal;
